import { Row, Col, Card, Tag, Typography, Spin, Alert } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";    
import { useEffect, useState } from 'react';
import axiosInstance from '../services/axios';
import "./Events.css";
import Heading from "./Heading";

const { Title, Text } = Typography;

const ProgramUi = ({ className = "" }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [highlightsData, setHighlightsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [thought, setThought] = useState(null);
  const [thoughtLoading, setThoughtLoading] = useState(false);
  const [thoughtError, setThoughtError] = useState(null);

  const handleViewAllClick = () => navigate("/all-announcements");

  // same axios fetching logic you gave me 👇
  useEffect(() => {
    let mounted = true;
    const fetchHighlights = async () => {
      setLoading(true);
      setError(null);
      try {
        const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';
        const resp = await axiosInstance.get('/programme-highlights/', {
          params: {  language: languageParam }
        });
        let items = [];
        if (Array.isArray(resp.data)) items = resp.data;
        else if (resp.data && typeof resp.data === 'object') {
          items = resp.data.results || resp.data.data || null;
          if (!items) {
            const values = Object.values(resp.data);
            if (Array.isArray(values) && values.length && typeof values[0] === 'object') items = values;
          }
        }
        if (!mounted) return;
        if (Array.isArray(items)) {
          const mapped = items
            .filter(it => it.is_active !== false)
            .map((it, idx) => ({
              id: it.id,
              tagText: it.type || '',
              date: it.date || '',
              description: it.title || '',
              link: it.link || null,
            }));
          setHighlightsData(mapped);
        } else {
          setHighlightsData([]);
          setError('Unexpected highlights response shape');
          console.error('/programme-highlights/ returned unexpected shape', resp.data);
        }
      } catch (err) {
        setError(err.message || 'Failed to load programme highlights');
        setHighlightsData([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchHighlights();
    return () => { mounted = false; };
  }, [i18n.language]);

  useEffect(() => {
    let mounted = true;
    const fetchThought = async () => {
      setThoughtLoading(true);
      setThoughtError(null);
      try {
        const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';
        const resp = await axiosInstance.get('/thought/', {
          params: {  language: languageParam }
        });
        let items = [];
        if (Array.isArray(resp.data)) items = resp.data;
        else if (resp.data && typeof resp.data === 'object') {
          items = resp.data.results || resp.data.data || null;
          if (!items) {
            const values = Object.values(resp.data);
            if (Array.isArray(values) && values.length && typeof values[0] === 'object') items = values;
          }
        }
        if (!mounted) return;
        if (Array.isArray(items) && items.length) {
          const active = items.find(it => it.is_active !== false) || items[0];
          setThought({ quote: active.quote, author: active.author, date: active.date });
        } else setThought(null);
      } catch (err) {
        setThoughtError(err.message || 'Failed to load thought');
        setThought(null);
      } finally {
        if (mounted) setThoughtLoading(false);
      }
    };
    fetchThought();
    return () => { mounted = false; };
  }, [i18n.language]);

  const tagColors = [
    "linear-gradient(135deg, #2B7FFF 0%, #002371 100%)",
    "linear-gradient(135deg, #00C950 0%, #006D29 100%)",
    "linear-gradient(135deg, #00B8DB 0%, #004A5D 100%)",
    "linear-gradient(135deg, #EC5042 0%, #B7131A 100%)",
  ];

  const highlights = highlightsData.length ? highlightsData : [];
  const displayedHighlights = highlights.slice(0, 4);

  return (
    <div className={`program-section ${className}`}>
      <Row className="program-header" justify="space-between" align="middle">
        <Col>
          <Heading
            content={t("events.title")}
          />
        </Col>
        <Col>
          <button className="program-viewall-btn max-sm:hidden" onClick={handleViewAllClick}>
            {t("events.viewAllButton")}
          </button>
        </Col>
      </Row>

      <div className="program-content">
        {loading ? (
          <Spin />
        ) : error ? (
          <Alert type="warning" message={error} />
        ) : (
          <Row gutter={[16, 16]}>
            {displayedHighlights.map((item, index) => (
              <Col xs={24} sm={12} md={12} lg={6} key={item.id || index}>
                <Card className="program-card">
                  <Row justify="space-between" align="top">
                    <Col>
                      <Tag
                        className="program-tag"
                        style={{ background: tagColors[index % tagColors.length] }}
                      >
                        {item.tagText}
                      </Tag>
                    </Col>
                    <Col>
                      <Text className="program-date">{item.date}</Text>
                    </Col>
                  </Row>
                  <Text className="program-desc">{item.description}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <Card className="program-thought">
          <Title level={4} className="program-thought-title">
            {t("events.thoughtTitle")}
          </Title>
          {thoughtLoading ? (
            <Spin />
          ) : thoughtError ? (
            <Alert type="warning" message={thoughtError} />
          ) : thought ? (
            <Text className="program-thought-quote">
              “{thought.quote}” — {thought.author}
            </Text>
          ) : (
            <Text className="program-thought-quote">
              {t("events.thoughtQuote")}
            </Text>
          )}
        </Card>
        <button className="program-viewall-btn mobile-view-btn max-sm:block hidden" onClick={handleViewAllClick}>
          {t("events.viewAllButton")}
        </button>
      </div>
    </div>
  );
};

export default ProgramUi;
